// ============================================
// COBROAVISOSPANEL.JS — "Alertas de cobro" del vendedor en Mi Trabajo (AUDITOR Parte C, 2026-10-21)
// ============================================
// Decision de Ricardo (21-oct): ademas del aviso dentro del expediente (CobroBanner del flujo), las alertas de cobro (B10)
// le llegan al VENDEDOR en su Mi Trabajo, como las garantias por vencer. El servidor las calcula al vuelo
// (GET /api/auditor/cobro/mias): nivel 1 material adquirido sin pago, 2 instalacion entregada sin cobrar, 3 servicio hecho
// sin cobrar, y DOBLE cuando ya salio material y no se ha cobrado. Las cifras (pagado/total) las manda el servidor SOLO a
// quien le tocan (el vendedor dueno, o quien ve los montos de todos en "Ver todos"). Si no hay alertas, el panel no se pinta.
// Responsive: tarjetas apiladas, botones >= 44 px.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { fetchAuth } from '../utils/sesion';
import { API_BASE } from '../config/api';
import { fmtDinero } from '../utils/dinero';
import { COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloTarjeta, estiloBotonSecundario } from './helpersClientes';

export default function CobroAvisosPanel({ todos = false, compacto = false, onAbrirExpediente }) {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await fetchAuth(`${API_BASE}/api/auditor/cobro/mias${todos ? '?todos=1' : ''}`);
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudieron leer las alertas de cobro');
      setLista(j.alertas || []); setError(null);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setLista([]); }
  }, [todos]);
  useEffect(() => { cargar(); }, [cargar]);

  // Sin alertas no hay nada que avisar: el panel no ocupa sitio en Mi Trabajo.
  if (!error && (!lista || lista.length === 0)) return null;
  const dobles = (lista || []).filter(a => a.doble).length;
  const btn = { ...estiloBotonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 };

  return (
    <div data-cobro="panel" style={{ ...estiloTarjeta, padding: compacto ? 12 : 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <AlertTriangle size={18} color={dobles ? '#dc2626' : '#9A5B00'} />
        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO }}>Alertas de cobro{lista ? ` (${lista.length})` : ''}</div>
        {dobles > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: '#dc2626', background: '#fde8e8', borderRadius: 999, padding: '2px 8px' }}>{dobles} doble{dobles === 1 ? '' : 's'}</span>}
        <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO, flexBasis: '100%' }}>Material entregado o trabajo hecho que el cliente no ha pagado. Desaparecen solas cuando se registra el cobro.</span>
      </div>
      {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {(lista || []).map(a => (
          <div key={a.oportunidad_id} data-cobro="item" style={{ border: `1px solid ${COLOR_BORDE}`, borderLeft: `4px solid ${a.doble ? '#dc2626' : '#f59e0b'}`, borderRadius: 10, padding: 12, background: a.doble ? '#fff7f7' : '#fff' }}>
            <div style={{ fontSize: 13, color: COLOR_OSCURO, lineHeight: 1.5 }}>
              <strong>{a.folio}</strong>{a.cliente_nombre ? <> · {a.cliente_nombre}{a.sucursal_nombre ? ` — ${a.sucursal_nombre}` : ''}</> : null}
              {a.doble && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, color: '#dc2626' }}>DOBLE ALERTA</span>}
            </div>
            {(a.niveles || []).map(n => <div key={n.nivel} style={{ fontSize: 12.5, color: COLOR_GRIS, marginTop: 2 }}><strong>Nivel {n.nivel}:</strong> {n.texto}</div>)}
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, display: 'flex', gap: '2px 12px', flexWrap: 'wrap' }}>
              {a.total != null && a.pagado != null && <span>Pagado {fmtDinero(a.pagado)} de {fmtDinero(a.total)}</span>}
              {todos && a.vendedor && <span>Vendedor: {a.vendedor}</span>}
              {a.titulo && <span>{a.titulo}</span>}
            </div>
            {onAbrirExpediente && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button type="button" style={btn} data-cobro="abrir" onClick={() => onAbrirExpediente(a.oportunidad_id)}>Abrir expediente</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
